import * as React from 'react'

import CreateApiKeyModal from './CreateApiKeyModal'
import ApiKeyRow from './ApiKeyRow'

import theme from 'theme'
import Button from 'components/common/Button'
import { Column } from 'components/common/Grid'
import {
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle
} from 'components/common/EmptyState'
import Icon from 'components/Icon'
import DataTable from 'components/common/DataTable'
import Text from 'components/common/Text'
import { ApiKeysResponse } from 'api'

const EmptyApiKeys: React.FC = () => (
  <EmptyState>
    <EmptyStateIcon>
      <Icon icon="key" />
    </EmptyStateIcon>
    <EmptyStateTitle>No API Keys</EmptyStateTitle>
  </EmptyState>
)

export const apiKeysColumns = {
  status: {
    span: 3,
    title: 'Status'
  },
  name: {
    span: 4,
    title: 'Name'
  },
  created: {
    span: 3,
    title: 'Created'
  },
  lastUsed: {
    span: 4,
    title: 'Last Used'
  },
  actions: {
    span: 2,
    title: ''
  }
}

const ApiKeysTableHeader: React.FC<{
  setIsCreateModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  apiKeyLimitReached: boolean
}> = ({ setIsCreateModalOpen, apiKeyLimitReached }) => {
  return (
    <>
      {apiKeyLimitReached && (
        <div
          data-test-api-keys-limit-banner
          css={{
            display: 'flex',
            flex: '1',
            alignItems: 'center',
            paddingTop: theme.space[2],
            paddingRight: theme.space[3],
            paddingBottom: theme.space[2],
            paddingLeft: theme.space[3],
            marginRight: theme.space[5],
            border: theme.border.base,
            borderRadius: theme.borderRadius[0],
            boxShadow: theme.boxShadows.medium,
            whiteSpace: 'pre-wrap'
          }}
        >
          <Icon
            css={{
              flexShrink: 0,
              color: theme.colors.steel[0],
              fontSize: theme.space[4]
            }}
            icon="info"
          />
          <Text as="p" color="medium" css={{ marginLeft: 8 }}>
            <b>Limit Reached: </b>
            Maximum of 5 active keys. To create a new key revoke a currently
            active key.
          </Text>
        </div>
      )}
      <Button
        data-test-open-api-key-modal
        css={{
          marginLeft: 'auto'
        }}
        disabled={apiKeyLimitReached}
        label="New API Key"
        onClick={() => setIsCreateModalOpen(true)}
      />
    </>
  )
}

function getActiveApiKeysCount(apiKeys) {
  return apiKeys.filter(({ state }) => state === 'ACTIVE').length
}

const ApiKeysTable: React.FC<{
  keys: ApiKeysResponse
  hasError: boolean
  isLoading: boolean
  doRefresh: () => void
  apiKeysLimit: number
}> = ({ keys, hasError, isLoading, doRefresh, apiKeysLimit }) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false)

  const apiKeyLimitReached = getActiveApiKeysCount(keys) >= apiKeysLimit

  return (
    <DataTable
      data-test-apikeys-table
      columnTitles={Object.values(apiKeysColumns).map(({ span, title }, i) => {
        return (
          <Column key={title || i} span={span}>
            <Text
              as="h6"
              css={{
                fontSize: theme.fontSizes[1]
              }}
            >
              {title}
            </Text>
          </Column>
        )
      })}
      data={keys}
      error={hasError}
      getRowKey={r => r.identifier}
      gridColumns="repeat(16, minmax(auto, 1fr))"
      loading={isLoading}
      renderEmpty={() => <EmptyApiKeys />}
      renderHeader={() => {
        return (
          <>
            <CreateApiKeyModal
              fetchApiKeys={doRefresh}
              isOpen={isCreateModalOpen}
              setOpenState={setIsCreateModalOpen}
            />
            <ApiKeysTableHeader
              apiKeyLimitReached={apiKeyLimitReached}
              setIsCreateModalOpen={setIsCreateModalOpen}
            />
          </>
        )
      }}
      renderRow={apiKey => (
        <ApiKeyRow
          apiKey={apiKey}
          apiKeyLimitReached={apiKeyLimitReached}
          doRefresh={doRefresh}
        />
      )}
    />
  )
}

export default ApiKeysTable
